'use client';
console.log('[DirectoryTable.tsx loaded]');

import { useState, useEffect, useMemo } from 'react';
import { useDynamicAirtableRecords } from '@/lib/airtable/client-hooks';
import { 
  getTableNameByKey,
  AIRTABLE_TABLES
} from '@/lib/airtable/config';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { useDirectory } from './DirectoryContext';
import React from 'react';
import { parseAttachments } from '@/lib/airtable/transform';
import { LoadingPattern } from "../ui/pattern-viewer";

interface DirectoryTableProps {
  tableName: string;
  filters?: Record<string, any>; // Optional filters prop
}

// Hook debug utility
let hookCounter = 0;
function useHookDebug(name: string) {
  hookCounter++;
  useEffect(() => {
    console.log(`[HookDebug] ${name} (hook #${hookCounter})`);
  }, []);
}

// Patch: Use static mock data instead of Airtable data for isolation testing
const mockData = [
  {
    id: 'rec1',
    'Provider': 'Mock CRO 1',
    'Product/Platform': 'Mock Platform 1',
    'Slide Scanning Instrument': 'Mock Scanner 1',
    'Analysis Software': 'Mock Software 1',
    'Company Name': 'Mock Biotech 1',
    'Company Location': 'Night City',
    'Company Website': 'https://mockbiotech1.com',
    'createdAt': '2023-01-01T00:00:00Z',
    'Type': 'Type A',
    'Services': 'Service X, Service Y',
    'Technology': ['Tech Alpha', 'Tech Beta'],
  },
  {
    id: 'rec2',
    'Provider': 'Mock CRO 2',
    'Product/Platform': 'Mock Platform 2',
    'Slide Scanning Instrument': 'Mock Scanner 2',
    'Analysis Software': 'Mock Software 2',
    'Company Name': 'Mock Biotech 2',
    'Company Location': 'Pacifica',
    'Company Website': 'https://mockbiotech2.com',
    'createdAt': '2023-02-01T00:00:00Z',
    'Type': 'Type B',
    'Services': 'Service Z',
    'Technology': ['Tech Gamma'],
  },
];

export default function DirectoryTable({ tableName: logicalTableKey, filters }: DirectoryTableProps) {
  // All hooks at the top, no conditionals
  const { 
    visibleColumns, 
    columnOrder, 
    currentPage, 
    setCurrentPageForTable, 
    itemsPerPage,
    filters: contextFilters
  } = useDirectory();
  const tableId = AIRTABLE_TABLES[logicalTableKey]?.id;
  if (!tableId) {
    console.error("[DirectoryTable] No tableId found for logical key:", logicalTableKey);
    return <div className="text-red-500">Error: Invalid or missing table selected.</div>;
  }
  const [schema, setSchema] = useState<any>(null);
  const [schemaLoading, setSchemaLoading] = useState(true);
  const [linkedRecordLookups, setLinkedRecordLookups] = useState<Record<string, Record<string, string>>>({});
  const [linkedLoading, setLinkedLoading] = useState(false);
  // Get filters for this table
  const tableFilters = filters || contextFilters[logicalTableKey] || {};
  // Pass filters to useDynamicAirtableRecords (for now, just pass as options)
  // TODO: Map filter state to Airtable query options (e.g., filterByFormula) in the backend/API
  const { data, isLoading, isError, error, refetch } = useDynamicAirtableRecords(logicalTableKey, tableFilters);
  // const data = mockData;
  // const isLoading = false;
  // const isError = false;
  // const error = undefined;

  const flattenedData = useMemo(() => (
    (data ?? []).filter(Boolean).map((record: any) => ({
      ...record,
      createdTime: record?.createdAt,
    }))
  ), [data]);

  // Sort Providers by 'Company' A-Z
  const sortedData = useMemo(() => {
    if (logicalTableKey === 'PROVIDERS') {
      return [...flattenedData].sort((a, b) => {
        const aName = (a['Company'] || '').toLowerCase();
        const bName = (b['Company'] || '').toLowerCase();
        return aName.localeCompare(bName);
      });
    }
    return flattenedData;
  }, [flattenedData, logicalTableKey]);

  const currentTableSchema = useMemo(() =>
    schema?.tables?.find((t: any) => t.id === tableId),
    [schema, tableId]
  );

  const linkedFields = useMemo(() => (
    currentTableSchema?.fields?.filter((f: any) => f.type === 'multipleRecordLinks') ?? []
  ), [currentTableSchema]);

  const lookupFields = useMemo(() => (
    currentTableSchema?.fields?.filter(
      (f: any) => f.type === 'lookup' || f.type === 'multipleLookupValues'
    ) ?? []
  ), [currentTableSchema]);

  // Debug: Log all lookup field definitions and their options
  useEffect(() => {
    if (lookupFields && lookupFields.length > 0) {
      console.log('[SchemaDebug] lookupFields:', lookupFields.map((f: any) => ({ name: f.name, type: f.type, options: f.options })));
    }
  }, [lookupFields]);

  const allFields = useMemo(() => {
    const fieldSet = new Set<string>();
    flattenedData.forEach((record: Record<string, any>) => {
      Object.keys(record).forEach(key => {
        if (key !== 'id' && key !== 'createdTime') {
          fieldSet.add(key);
        }
      });
    });
    return Array.from(fieldSet);
  }, [flattenedData]);

  const visibleCols = useMemo(() => (
    visibleColumns[tableId]?.length > 0 ? visibleColumns[tableId] : allFields
  ), [visibleColumns, tableId, allFields]);

  const orderedCols = useMemo(() => {
    if (currentTableSchema && currentTableSchema.fields) {
      return currentTableSchema.fields.map((f: any) => f.name);
    }
    // Fallback: use allFields if schema is missing
    return allFields;
  }, [currentTableSchema, allFields]);

  // Restore useEffect for fetching schema
  useEffect(() => {
    setSchemaLoading(true);
    fetch('/api/airtable/schema')
      .then(res => res.json())
      .then(data => {
        setSchema(data); // Store the WHOLE schema, not just one table
      })
      .catch(() => setSchema(null))
      .finally(() => setSchemaLoading(false));
  }, []);

  // Re-enable fetching linked record lookups for display names
  useEffect(() => {
    async function fetchLinkedLookups() {
      if (!currentTableSchema) {
        setLinkedRecordLookups({});
        return;
      }
      setLinkedLoading(true);
      const lookups: Record<string, Record<string, string>> = {};
      try {
        // 1. Build lookup maps for all direct linked fields
        for (const field of linkedFields) {
          const linkedTableId = field.options?.linkedTableId || field.options?.linkedTableName;
          if (!linkedTableId) continue;
          if (lookups[linkedTableId]) continue; // Already fetched
          const res = await fetch(`/api/airtable?table=${linkedTableId}`);
          const apiResponse = await res.json();
          const records = Array.isArray(apiResponse) ? apiResponse : apiResponse.records;
          if (!Array.isArray(records)) continue;
          // Get the primary field id and name from the schema
          const linkedTableSchema = schema?.tables?.find((t: any) => t.id === linkedTableId);
          const primaryFieldId = linkedTableSchema?.primaryFieldId;
          const primaryFieldName = linkedTableSchema?.fields?.find((f: any) => f.id === primaryFieldId)?.name;
          const map: Record<string, string> = {};
          for (const rec of records) {
            let display = (primaryFieldName && rec[primaryFieldName]) ? rec[primaryFieldName] : rec.id;
            map[rec.id] = display;
          }
          lookups[linkedTableId] = map;
        }
        // 2. Build lookup maps for all lookup fields' target tables (including chained lookups)
        for (const field of lookupFields) {
          let lookupLinkedTableId = field.options?.linkedTableId;

          // Handle chained lookups (recordLinkFieldId + fieldIdInLinkedTable)
          if (!lookupLinkedTableId && field.options?.recordLinkFieldId && field.options?.fieldIdInLinkedTable) {
            // 1. Find the source linked field in the current table schema
            const sourceLinkedField = currentTableSchema?.fields?.find(
              (f: any) => f.id === field.options.recordLinkFieldId
            );
            const intermediateTableId = sourceLinkedField?.options?.linkedTableId;
            // 2. Find the field in the intermediate table schema
            const intermediateTableSchema = schema?.tables?.find((t: any) => t.id === intermediateTableId);
            const finalLinkedField = intermediateTableSchema?.fields?.find(
              (f: any) => f.id === field.options.fieldIdInLinkedTable
            );
            lookupLinkedTableId = finalLinkedField?.options?.linkedTableId;
            console.log('[SchemaDebug] [ChainedLookup] For', field.name, 'final linked table:', lookupLinkedTableId);
          } else if (!lookupLinkedTableId && field.options?.recordLinkFieldId) {
            // Fallback: single-hop (legacy)
            const sourceLinkedField = currentTableSchema?.fields?.find(
              (f: any) => f.id === field.options.recordLinkFieldId
            );
            lookupLinkedTableId = sourceLinkedField?.options?.linkedTableId;
          }

          if (!lookupLinkedTableId) continue;
          if (lookups[lookupLinkedTableId]) continue; // Already fetched

          const res = await fetch(`/api/airtable?table=${lookupLinkedTableId}`);
          const apiResponse = await res.json();
          const records = Array.isArray(apiResponse) ? apiResponse : apiResponse.records;
          if (!Array.isArray(records)) continue;
          // Get the primary field name from the schema
          const lookupTableSchema = schema?.tables?.find((t: any) => t.id === lookupLinkedTableId);
          const primaryFieldName = lookupTableSchema?.fields?.[0]?.name;
          const map: Record<string, string> = {};
          for (const rec of records) {
            let display = (primaryFieldName && rec[primaryFieldName]) ? rec[primaryFieldName] : rec.id;
            map[rec.id] = display;
          }
          lookups[lookupLinkedTableId] = map;
        }
        console.log('[LinkedLookup] lookups:', lookups);
        setLinkedRecordLookups(lookups);
      } catch (err) {
        console.error('[LinkedLookup] Error fetching lookups:', err);
        setLinkedRecordLookups({});
      } finally {
        setLinkedLoading(false);
      }
    }
    fetchLinkedLookups();
    // Only rerun if schema or field names change
  }, [
    currentTableSchema?.id,
    linkedFields.map((f: any) => f.name).join(','),
    lookupFields.map((f: any) => f.name).join(',')
  ]);

  // Ultra-verbose logging for Airtable data
  React.useEffect(() => {
    console.log('[DirectoryTable][Airtable data] raw data:', data);
    console.log('[DirectoryTable][Airtable data] flattenedData:', flattenedData);
    if (flattenedData && flattenedData.length > 0) {
      flattenedData.forEach((record: any, idx: number) => {
        console.log(`[DirectoryTable][Airtable data][record ${idx}]`, record);
      });
    }
    if (schema) {
      console.log('[DirectoryTable][Airtable schema]', schema);
    }
    if (linkedRecordLookups) {
      console.log('[DirectoryTable][LinkedRecordLookups]', linkedRecordLookups);
    }
  }, [data, flattenedData, schema, linkedRecordLookups]);

  if (schemaLoading || isLoading || linkedLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <LoadingPattern patternName="cell-pattern" size="md" />
      </div>
    );
  }

  if (isError) {
    return <div className="text-red-500">Error loading data</div>;
  }

  if (!flattenedData || flattenedData.length === 0) {
    return <div className="text-gray-500">No data available</div>;
  }

  // Render the main table (with formatted rendering for each cell)
  return (
    <div style={{ overflowX: 'auto', width: '100%' }} className="directory-table-scroll">
      <table
        className="min-w-full border-collapse table-auto"
        style={{ tableLayout: 'auto' }}
      >
        <thead>
          <tr>
            {orderedCols.map((col: string) => (
              <th
                key={col}
                className="px-4 py-2 text-left bg-background border-b border-gray-700"
                style={{ minWidth: 210, maxWidth: 600, fontWeight: 600, wordBreak: 'break-word' }}
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedData.filter(Boolean).map((record: any, rowIdx: number) => {
            if (!record) {
              // Only warn if a truly unexpected null/undefined record is encountered
              return null;
            }
            return (
              <tr key={record.id || rowIdx} className="border-b border-gray-800">
                {orderedCols.map((col: string, colIdx: number) => {
                  // Special case: Providers - merged Company column (logo + name + location)
                  if (logicalTableKey === 'PROVIDERS' && colIdx === 0) {
                    return (
                      <td
                        key={colIdx}
                        className="px-4 py-2 whitespace-nowrap align-top"
                        style={{ minWidth: 210, maxWidth: 600, wordBreak: 'break-word', whiteSpace: 'normal' }}
                      >
                        <div className="flex items-center gap-3">
                          {record['Logo'] && Array.isArray(record['Logo']) && record['Logo'][0]?.url && (
                            <img src={record['Logo'][0].url} alt={record['Company'] || 'Logo'} className="w-10 h-10 object-contain rounded border border-border bg-gray-800" style={{ maxWidth: 40, maxHeight: 40 }} />
                          )}
                          <div>
                            <a href={record['Company Website']} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline text-lg font-bold">
                              {record['Company']}
                            </a>
                            {record['Location'] && (
                              <div className="text-xs text-muted-foreground mt-1">{record['Location']}</div>
                            )}
                          </div>
                        </div>
                      </td>
                    );
                  }
                  // For all other columns, render as pills (including linked/lookup fields)
                  let cellValue = record[col];
                  const isLinked = linkedFields.some((f: any) => f.name === col);
                  const isLookup = lookupFields.some((f: any) => f.name === col);
                  const lookup = (() => {
                    if (isLinked) {
                      const linkedTableId = linkedFields.find((f: any) => f.name === col)?.options?.linkedTableId;
                      if (!linkedTableId) return undefined;
                      return linkedRecordLookups[linkedTableId];
                    } else if (isLookup) {
                      const lookupFieldDef = lookupFields.find((f: any) => f.name === col);
                      if (!lookupFieldDef) return undefined;
                      let lookupLinkedTableId = lookupFieldDef?.options?.linkedTableId;
                      if (!lookupLinkedTableId && lookupFieldDef?.options?.recordLinkFieldId && lookupFieldDef?.options?.fieldIdInLinkedTable) {
                        const sourceLinkedField = currentTableSchema?.fields?.find((f: any) => f.id === lookupFieldDef.options.recordLinkFieldId);
                        if (!sourceLinkedField) return undefined;
                        const intermediateTableId = sourceLinkedField?.options?.linkedTableId;
                        if (!intermediateTableId) return undefined;
                        const intermediateTableSchema = schema?.tables?.find((t: any) => t.id === intermediateTableId);
                        if (!intermediateTableSchema) return undefined;
                        const finalLinkedField = intermediateTableSchema?.fields?.find((f: any) => f.id === lookupFieldDef.options.fieldIdInLinkedTable);
                        if (!finalLinkedField) return undefined;
                        lookupLinkedTableId = finalLinkedField?.options?.linkedTableId;
                        if (!lookupLinkedTableId) return undefined;
                        return linkedRecordLookups[lookupLinkedTableId];
                      } else if (!lookupLinkedTableId && lookupFieldDef?.options?.recordLinkFieldId) {
                        const sourceLinkedField = currentTableSchema?.fields?.find((f: any) => f.id === lookupFieldDef.options.recordLinkFieldId);
                        if (!sourceLinkedField) return undefined;
                        lookupLinkedTableId = sourceLinkedField?.options?.linkedTableId;
                        if (!lookupLinkedTableId) return undefined;
                        return linkedRecordLookups[lookupLinkedTableId];
                      }
                      if (!lookupLinkedTableId) return undefined;
                      return linkedRecordLookups[lookupLinkedTableId];
                    }
                    return undefined;
                  })();
                  const linkedTableId = isLinked
                    ? linkedFields.find((f: any) => f.name === col)?.options?.linkedTableId
                    : isLookup
                      ? lookupFields.find((f: any) => f.name === col)?.options?.linkedTableId
                      : undefined;
                  let finalLinkedTableId = undefined;
                  if (isLookup) {
                    const lookupFieldDef = lookupFields.find((f: any) => f.name === col);
                    if (lookupFieldDef?.options?.recordLinkFieldId && lookupFieldDef?.options?.fieldIdInLinkedTable) {
                      const sourceLinkedField = currentTableSchema?.fields?.find((f: any) => f.id === lookupFieldDef.options.recordLinkFieldId);
                      const intermediateTableId = sourceLinkedField?.options?.linkedTableId;
                      const intermediateTableSchema = schema?.tables?.find((t: any) => t.id === intermediateTableId);
                      const finalLinkedField = intermediateTableSchema?.fields?.find((f: any) => f.id === lookupFieldDef.options.fieldIdInLinkedTable);
                      finalLinkedTableId = finalLinkedField?.options?.linkedTableId;
                    } else if (lookupFieldDef?.options?.recordLinkFieldId) {
                      const sourceLinkedField = currentTableSchema?.fields?.find((f: any) => f.id === lookupFieldDef.options.recordLinkFieldId);
                      finalLinkedTableId = sourceLinkedField?.options?.linkedTableId;
                    } else {
                      finalLinkedTableId = lookupFieldDef?.options?.linkedTableId;
                    }
                  }
                  // Get the formatted value (linked/lookup/regular)
                  let formattedValue = isLinked
                    ? formatLinkedCellValue(cellValue, lookup, linkedTableId)
                    : isLookup
                      ? (Array.isArray(cellValue) && (typeof cellValue[0] === 'string' || typeof cellValue[0] === 'number')
                          ? Array.from(new Set(cellValue)) // Render unique primitives as pills
                          : (() => {
                              // For lookups of objects/IDs, dedupe by id or value
                              if (Array.isArray(cellValue)) {
                                const seen = new Set();
                                return cellValue.filter(item => {
                                  const key = typeof item === 'object' && item !== null ? (item.id || item.Name || item.name) : item;
                                  if (seen.has(key)) return false;
                                  seen.add(key);
                                  return true;
                                });
                              }
                              return formatLookupCellValue(cellValue, lookup, linkedTableId, col, linkedRecordLookups, finalLinkedTableId);
                            })())
                      : formatCellValue(cellValue, col, record.id);
                  // Render as pills (array or single value)
                  const renderPills = (val: any) => {
                    if (Array.isArray(val)) {
                      return val.map((v: any, i: number) => (
                        <span key={i} className="inline-block bg-blue-900 text-blue-100 rounded-md px-4 py-2 text-xs font-semibold mr-1 mb-1">
                          {String(v)}
                        </span>
                      ));
                    }
                    if (typeof val === 'string' && val.trim() !== '') {
                      return (
                        <span className="inline-block bg-blue-900 text-blue-100 rounded-md px-4 py-2 text-xs font-semibold mr-1 mb-1">
                          {val}
                        </span>
                      );
                    }
                    return val;
                  };
                  return (
                    <td
                      key={`${record.id ?? 'unknown'}-${col}`}
                      className="px-4 py-2 whitespace-nowrap align-top"
                      style={{ minWidth: 210, maxWidth: 600, wordBreak: 'break-word', whiteSpace: 'normal' }}
                    >
                      {renderPills(formattedValue)}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Defensive patch for .toLocaleDateString()
function formatCellValue(value: any, fieldName?: string, recordId?: string): React.ReactNode {
  if (fieldName === 'to') {
    console.warn(`[DirectoryTable] Field named 'to' detected in record ${recordId}:`, value);
  }
  if (value === undefined || value === null) {
    if (fieldName) {
      console.warn(`[DirectoryTable] Undefined/null value for field '${fieldName}' in record ${recordId}`);
    }
    return '-';
  }

  // Special case: Company Website as clickable link
  if (fieldName === 'Company Website' && typeof value === 'string') {
    let url = value;
    if (!/^https?:\/\//i.test(url)) {
      url = 'https://' + url;
    }
    // Extract domain for display
    let display = url.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '');
    display = display.split('/')[0]; // Only domain
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-400 underline break-all">
        {display}
      </a>
    );
  }

  // Handle Airtable attachment arrays
  if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && value[0].url && value[0].filename) {
    // Use parseAttachments to normalize
    const attachments = parseAttachments(value);
    if (!attachments || attachments.length === 0) return '-';
    return (
      <div className="flex flex-row gap-2 items-center">
        {attachments.map(att =>
          att.type.startsWith('image/') ? (
            <a key={att.id} href={att.url} target="_blank" rel="noopener noreferrer">
              <img
                src={att.thumbnailSmall || att.url}
                alt={att.filename}
                title={att.filename}
                className="w-12 h-12 object-contain rounded border border-border bg-gray-800"
                style={{ maxWidth: 48, maxHeight: 48 }}
              />
            </a>
          ) : (
            <a key={att.id} href={att.url} target="_blank" rel="noopener noreferrer" className="text-blue-400 underline">
              {att.filename}
            </a>
          )
        )}
      </div>
    );
  }

  // Handle arrays (non-attachment)
  if (Array.isArray(value)) {
    if (value.length > 0 && typeof value[0] === 'object') {
      return value
        .map(item => item?.name || item?.Name || item?.id)
        .filter(Boolean)
        .join(', ');
    }
    return value.join(', ');
  }

  // Handle objects
  if (typeof value === 'object') {
    const displayValue =
      value.name ||
      value.Name ||
      value['Company Name'] ||
      value.Provider ||
      value.Product ||
      value.id;
    return displayValue || '[object]';
  }

  // Defensive date handling
  if (typeof value === 'string' && fieldName && fieldName.toLowerCase().includes('date')) {
    const date = new Date(value);
    if (!isNaN(date.getTime()) && date && typeof date.toLocaleDateString === 'function') {
      try {
        return date.toLocaleDateString();
      } catch (err) {
        console.error(`[DirectoryTable] Error calling toLocaleDateString for field '${fieldName}' in record ${recordId}:`, value, err);
        return value ?? '-';
      }
    } else {
      console.warn(`[DirectoryTable] Invalid date for field '${fieldName}' in record ${recordId}:`, value);
      return value ?? '-';
    }
  }

  // Default to string representation
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  // Fallback for unknown types
  console.warn(`[DirectoryTable] Unknown value type for field '${fieldName}' in record ${recordId}:`, value);
  return '-';
}

// Helper to format linked record cell values
function formatLinkedCellValue(value: any, lookup: Record<string, string> | undefined, linkedTableId?: string): React.ReactNode {
  if (value === undefined || value === null) {
    return '-';
  }
  if (!lookup) return '-';

  // Only use the lookup map for display
  if (Array.isArray(value)) {
    // Return array of display values, not a joined string
    return value.map((item: any) => lookup[item] || item);
  }
  if (typeof value === 'string') {
    return lookup[value] || value;
  }
  return '-';
}

// Add a new helper for lookup fields
function formatLookupCellValue(
  value: any,
  lookup: Record<string, string> | undefined,
  linkedTableId?: string,
  fieldName?: string,
  allLookups?: Record<string, Record<string, string>>,
  finalLinkedTableId?: string
): React.ReactNode {
  if (value === undefined || value === null) {
    return '-';
  }
  // Only use the lookup map for display
  const map = finalLinkedTableId && allLookups && allLookups[finalLinkedTableId] ? allLookups[finalLinkedTableId] : lookup;
  if (!map) return '-';
  if (Array.isArray(value)) {
    // Return array of display values, not a joined string
    return value.map((item: any) => map[item] || item);
  }
  if (typeof value === 'string') {
    return map[value] || value;
  }
  return '-';
}

// Loading skeleton
function TableSkeleton() {
  return (
    <div className="space-y-4">
      <div className="border rounded-md overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {Array.from({ length: 5 }).map((_, i) => (
                <TableHead key={i}>
                  <Skeleton className="h-4 w-20" />
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: 10 }).map((_, rowIndex) => (
              <TableRow key={rowIndex}>
                {Array.from({ length: 5 }).map((_, cellIndex) => (
                  <TableCell key={`${rowIndex}-${cellIndex}`}>
                    <Skeleton className="h-4 w-full" />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <Skeleton className="h-10 w-full" />
    </div>
  );
}

// Add a global error boundary for this directory page
export function ErrorBoundary({ error }: { error: Error }) {
  return (
    <div style={{ color: 'red', padding: 24 }}>
      <h2>Something went wrong in the Directory.</h2>
      <pre>{error.message}</pre>
    </div>
  );
} 
